/* DESIGN CONSTANT INSTRUCTIONS __________________________________________________________

    Please check the files you touch:
        - Please replace any values with the ones from here.
        - Use whichever color or space is the closest.
        - If there is no value that fits your variable, check the p6 design and create it.

    Content:
        - colors:
            - P6: their specific colors like the signature orange
            - all kinds of greys
            - backgrounds: colors that are used as background
            - transparent colors
        - opacity: values like 0.5 used as opacity
        - borders: kinds of borders
        - border radius
        - font sizes: currently named after their values
        - font weights
        - spaces: meaningful important ones only

*/

const DesignConstants = {
    COLORS: {
        // P6 specific colors from their color palettes - - -
        P6_ORANGE_DARKER: "#CC6600", // PRIMARY 500
        P6_ORANGE: "#fc7c00",
        P6_ORANGE_BRIGHTER: "#fca24c", // as used in gradients
        P6_ORANGE_PASTEL: "#fcca97",
        P6_ORANGE_MID_PASTEL: "#FDE3C8",
        P6_ORANGE_LIGHT_PASTEL: "#FEF5EC",
        P6_ORANGE_LIGHT_25: "rgba(252, 124, 0, 0.25)",

        // additional colors - - - - - - - - - - - - - - - -
        // add here
        PURPLE: "#af00cc",
        RED: "#eb3800",
        RED_PASTEL: "#FFF2EE",
        RED_BRIGHTER: "#ff7b52",
        YELLOW: "#f5cc00",
        GREEN_BRIGHTER: "#9dda6c",
        GREEN: "#4c8321",
        BLUE: "#00a3ff",
        WHITE: "#ffffff",

        P6_SUCCESS_400: "#69B42D",
        P6_SUCCESS_500: "#4C8321",

        FOREST_GREEN_800: "#007A50",
        FOREST_GREEN_900: "#0D5436",
        FOREST_GREEN_PASTEL: "#F3FFF3",
        FOREST_GREEN_LIGHT_25: "rgba(0, 122, 80, 0.25)", // FOREST_GREEN_800 at 25% opacity

        //greys - - - - - - - - - - - - - - - - - - - - - - -
        LIGHTEST_GREY: "#e8e8e8", // NEUTRAL 200, used for borders
        LIGHT_GREY: "#CCCCCC", // NEUTRAL 250

        MID_GREY: "#999999", // NEUTRAL 300, used for some borders and lines

        DARK_GREY: "#666666", // NEUTRAL 400, used as font color
        DARKEST_GREY: "#333333", // NEUTRAL 500

        // background colors - - - - - - - - - - - - - - - -
        BACKGROUND_LIGHT: "#f6f6f6", //NEUTRAL 100 a very light grey for backgrounds

        // transparent colors - - - - - - - - - - - - - - -
        // try to use one of the greys above, but
        // - if this is not possible or
        // - it's a shadow
        // please feel free to add your transparent color here:
        TRANSPARENT_BLACK: "rgba(0,0,0,0.1)", // some kind of special grey
        TRANSPARENT_WHITE: "rgba(0,0,0,0.4)",
        TRANSPARENT_GREY: "#6666660d",

        TRANSPARENT_SUFFIX: "11",
    },
    OPACITY: {
        // 1 (visible) and 0 (invisible) don't have to be added here
        THREE: 0.3,
        HALF: 0.5,
        SIX: 0.6,
        SEVEN: 0.7,
    },
    BORDERS: {
        // needs to be in this order (px solid) for it to work in react pdf
        THIN: "1px solid",
        SEMI_THIN: "1.5px solid",
        MEDIUM: "2px solid",

        // more specific borders - - - - - - - - - - - - - -
        THIN_GREY: "1px solid #e8e8e8",
    },
    BORDER_RADIUS: {
        BUTTON: "25px", // as used in a StyledButton
        PILL: "100px", // pill-shaped elements (dropdowns, tags) that need maximum rounding
        SMALL_CONTAINER: "1rem", // a small container too high to be rounded 100% on its edges
        XSMALL_CONTAINER: "0.5rem",
    },
    FONT_SIZES: {
        // named after their numbers for now. Will be minimized and renamed in a future issue.

        XXS: "0.6rem", // around 10px
        XS: "0.75rem", // around 12px
        S: "0.9rem", // around 14px SMALL
        M: "1rem", //16px
        L: "1.125rem", // 18px LARGE
        XL: "1.5rem", // 24px XLARGE
        XXL: "2rem", // 32px

        INHERIT: "inherit",
    },
    FONT_WEIGHTS: {
        NORMAL: 400,
        MEDIUM: 500,
        SEMI_BOLD: 600,
        BOLD: 700,
        EXTRA_BOLD: 800,
        BLACK: 900,
    },
    SPACES: {
        /* meaningful margins and paddings that get reused a lot. For now only:
            - margins between white boxes and / or buttons
            - paddings within these white boxes.
        */
        // new - please use these where possible
        XXS: "0.25rem", // 4px
        XS: "0.5rem", // 8px
        S: "1rem", // 16px, small standard gap between interactive elements e.g. between buttons or exercise types
        M: "1.5rem", // 24px
        L: "2rem", // 32px
        XL: "2.5rem", // 40px
        XXL: "3rem", // 48px
        XXXL: "4rem", // 64px
        XXXXL: "5rem", // 80px
        XXXXXL: "6rem", // 96px
    },
    SIZES: {
        ICON_XXS: "1rem",
        ICON_XS: "2.2rem",
        ICON_M: "3rem",
        ICON_L: "3.5rem",
    },
    PDF: {
        FONTSIZE_XXS: 8,
        FONTSIZE_XS: 9,
        FONTSIZE_S: 10,
        FONTSIZE_REGULAR: 11,
        FONTSIZE_EXERCISE_HEADER: 12,
        FONTSIZE_EMPHASIZED_HEADER: 15,
        FONTSIZE_PAGE_HEADER: 18,

        GAP_XXS: "0.125cm",
        GAP_XS: "0.25cm",
        GAP_S: "0.5cm",
        GAP_M: "1cm",
        GAP_L: "1.5cm",
        GAP_XL: "2cm",
        GAP_XXL: "2.5cm",
        GAP_XXXL: "2.8cm",

        PROXIMA_FONT: "ProximaNovaPDF",
        REGULAR: 400,
        SEMIBOLD: 600,

        BORDER_WIDTH_S: 1,
        BORDER_WIDTH_M: 2,

        BORDER_RADIUS_S: 10,
        BORDER_RADIUS_M: 20,

        GREY: "#cccccc", //used for lines

        MIN_PRESENCE_AHEAD: 60, // this part of the following sibling elements (height)
        // needs to be on the same page
    },
    BREAKPOINTS: {
        SM: 640, // Tailwind sm
        MD: 768, // Tailwind md
        LG: 1024, // Tailwind lg
        XL: 1280, // Tailwind xl
        XXL: 1536, // Tailwind 2xl
    },
};

export default DesignConstants;
